<template>
  <v-container fluid>

    <!-- Page title -->
    <v-row class="mb-2">
      <v-col>
        <h2 class="text--primary" style="font-size:1.2rem; font-weight:600;">
          <v-icon class="mr-2" color="primary">mdi-robot</v-icon>
          {{ $t('aichat.title') || 'AI Assistant' }}
        </h2>
        <p class="text--secondary caption mb-0">
          {{ $t('aichat.subtitle') || 'Ask questions about Malaysian HR statutory rules, EPF, SOCSO, EIS, PCB and more.' }}
        </p>
      </v-col>
    </v-row>

    <!-- Chat window -->
    <v-card outlined style="height: 60vh; display: flex; flex-direction: column;">

      <!-- Messages area -->
      <v-card-text
        ref="chatWindow"
        style="flex: 1; overflow-y: auto; padding: 16px;"
      >
        <!-- Welcome message when empty -->
        <div v-if="messages.length === 0" class="text-center mt-8">
          <v-icon size="48" color="grey lighten-1">mdi-robot-outline</v-icon>
          <p class="grey--text mt-3">
            {{ $t('aichat.welcome') || 'Hi! Ask me anything about HR statutory rules or Malaysian employment regulations.' }}
          </p>
          <!-- Suggestion chips -->
          <v-row justify="center" class="mt-4">
            <v-chip
              v-for="(s, i) in suggestions"
              :key="i"
              class="ma-1"
              outlined
              small
              color="primary"
              @click="sendSuggestion(s)"
            >{{ s }}</v-chip>
          </v-row>
        </div>

        <!-- Message bubbles -->
        <div v-for="(msg, i) in messages" :key="i" class="mb-3">

          <!-- User message -->
          <div v-if="msg.role === 'user'" class="d-flex justify-end">
            <v-card
              color="primary"
              dark
              class="pa-3"
              style="max-width:75%; border-radius:12px 12px 2px 12px;"
              elevation="0"
            >
              <span style="white-space: pre-wrap; font-size:0.9rem;">{{ msg.content }}</span>
            </v-card>
          </div>

          <!-- AI message -->
          <div v-else class="d-flex justify-start">
            <v-avatar size="28" :color="msg.limitReached ? 'orange lighten-4' : 'grey lighten-3'" class="mr-2 mt-1">
              <v-icon small :color="msg.limitReached ? 'orange darken-2' : 'primary'">
                {{ msg.limitReached ? 'mdi-alert-circle' : 'mdi-robot' }}
              </v-icon>
            </v-avatar>
            <tara-attrition-card
              v-if="msg.cardType === 'attrition'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <tara-headcount-card
              v-else-if="msg.cardType === 'headcount'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <tara-payroll-card
              v-else-if="msg.cardType === 'payroll'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <tara-payroll-select-card
              v-else-if="msg.cardType === 'payroll_select'"
              :data="msg.cardData"
              @select-payroll="onPayrollSelected"
            />
            <tara-workforce-cost-card
              v-else-if="msg.cardType === 'workforce_cost'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <tara-absenteeism-card
              v-else-if="msg.cardType === 'absenteeism'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <v-card
              v-else
              outlined
              class="pa-3"
              :style="`max-width:75%; border-radius:2px 12px 12px 12px; ${msg.limitReached ? 'background:#FFF3E0; border-color:#FF9800;' : ''}`"
              elevation="0"
            >
              <span style="white-space: pre-wrap; font-size:0.9rem; line-height:1.6;">{{ msg.content }}</span>
            </v-card>
          </div>

        </div>

        <!-- Typing indicator -->
        <div v-if="loading" class="d-flex justify-start mt-2">
          <v-avatar size="28" color="grey lighten-3" class="mr-2">
            <v-icon small color="primary">mdi-robot</v-icon>
          </v-avatar>
          <v-card outlined class="pa-3" style="border-radius:2px 12px 12px 12px;" elevation="0">
            <v-progress-circular indeterminate size="16" width="2" color="primary" />
            <span class="ml-2 caption grey--text">{{ $t('aichat.thinking') || 'Thinking...' }}</span>
          </v-card>
        </div>

      </v-card-text>

      <v-divider />

      <!-- Input area -->
      <v-card-actions class="pa-3">
        <v-textarea
          v-model="inputText"
          :placeholder="$t('aichat.inputplaceholder') || 'Ask a question... (Enter to send)'"
          outlined
          dense
          auto-grow
          rows="1"
          hide-details
          :disabled="loading"
          @keydown.enter.exact.prevent="sendMessage"
          style="flex:1;"
        />
        <v-btn
          color="primary"
          icon
          large
          class="ml-2"
          :disabled="!inputText.trim() || loading"
          @click="sendMessage"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
        <v-btn
          icon
          class="ml-1"
          title="Clear chat"
          :disabled="messages.length === 0 || loading"
          @click="clearChat"
        >
          <v-icon small>mdi-delete-outline</v-icon>
        </v-btn>
      </v-card-actions>

    </v-card>

    <!-- Error snackbar -->
    <v-snackbar v-model="isErr" color="error" top :timeout="5000">
      {{ errMsg }}
      <template v-slot:action>
        <v-btn text @click="isErr = false">{{ $t('button.close') || 'Close' }}</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import TaraAttritionCard from "./TaraAttritionCard.vue";
import TaraHeadcountCard from "./TaraHeadcountCard.vue";
import TaraPayrollCard from "./TaraPayrollCard.vue";
import TaraPayrollSelectCard from "./TaraPayrollSelectCard.vue";
import TaraWorkforceCostCard from "./TaraWorkforceCostCard.vue";
import TaraAbsenteeismCard from "./TaraAbsenteeismCard.vue";

export default {
  name: "AIChatPanel",
  components: { "tara-attrition-card": TaraAttritionCard, "tara-headcount-card": TaraHeadcountCard, "tara-payroll-card": TaraPayrollCard, "tara-payroll-select-card": TaraPayrollSelectCard, "tara-workforce-cost-card": TaraWorkforceCostCard, "tara-absenteeism-card": TaraAbsenteeismCard },

  data() {
    return {
      inputText: "",
      loading: false,
      isErr: false,
      errMsg: "",
      suggestions: [
        "What is the current EPF contribution rate?",
        "Who is eligible for SOCSO?",
        "What is the EIS contribution rate?",
        "How is PCB calculated?",
        "What are the annual leave entitlements under the Employment Act?",
        "Who might be leaving the company soon?",
      ],
    };
  },

  computed: {
    messages() {
      return this.$store.state.taraMessages;
    }
  },

  methods: {
    async onPayrollSelected(selection) {
      // User picked a payroll run — send request with poid embedded
      try {
        this.loading = true;
        this.$store.commit("addTaraMessage", { role: "user", content: `Check payroll: ${selection.payoutdesc} (${selection.pyyear}/${String(selection.pymonth).padStart(2,'0')})` });
        const res = await this.$store.dispatch("post", {
          url: "/ai/chat",
          payload: {
            messages:        this.messages.filter(m => m.content !== null && m.content !== undefined),
            compnyid:        this.$store.state.compnyid || 0,
            staffid:         this.$store.state.staffId  || this.$store.getters.staffId  || null,
            userid:          this.$store.state.userId   || this.$store.getters.userId   || null,
            payrollPoid:     selection.poid,
            payrollPrevPoid: null,
          },
        });
        if (res.data.status === 200) {
          if (res.data.cardType === "payroll") {
            this.$store.commit("addTaraMessage", { role: "assistant", content: null, cardType: "payroll", cardData: res.data.cardData });
          } else if (res.data.cardType === "payroll_select") {
            this.$store.commit("addTaraMessage", { role: "assistant", content: null, cardType: "payroll_select", cardData: res.data.cardData });
          } else {
            this.$store.commit("addTaraMessage", { role: "assistant", content: res.data.reply || "Unable to retrieve payroll anomalies." });
          }
        } else {
          this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I encountered an error checking the payroll." });
        }
      } catch (err) {
        console.error("[TARA] onPayrollSelected error:", err);
        this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I encountered an error checking the payroll." });
      } finally {
        this.loading = false;
      }
    },
    sendSuggestion(text) {
      this.inputText = text;
      this.sendMessage();
    },

    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.loading) return;

      this.$store.commit("addTaraMessage", { role: "user", content: text });
      this.inputText = "";
      this.loading = true;
      this.$nextTick(() => this.scrollToBottom());

      try {
        // Use same $store.dispatch pattern as all other components
        const res = await this.$store.dispatch("post", {
          url: "/ai/chat",
          payload: {
            messages: this.messages.filter(m => m.content !== null && m.content !== undefined),
            compnyid: this.$store.state.compnyid || 0,
            staffid: this.$store.state.staffId || this.$store.getters.staffId || null,
            userid: this.$store.state.userId || this.$store.getters.userId || null,
          },
        });

        if (res.data.status === 200) {
          if (res.data.cardType === "attrition") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "attrition",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "headcount") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "headcount",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "payroll") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "payroll",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "payroll_select") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "payroll_select",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "workforce_cost") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "workforce_cost",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "absenteeism") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "absenteeism",
              cardData: res.data.cardData,
            });
          } else {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: res.data.reply,
              limitReached: res.data.limitReached || false,
            });
            if (res.data.usageWarning) {
              this.showError(res.data.usageWarning);
            }
          }
        } else {
          this.showError(res.data.error || "AI service error");
          this.$store.commit("clearTaraMessages");
        }
      } catch (err) {
        this.showError("Cannot connect to AI service. Please try again.");
        this.$store.commit("clearTaraMessages");
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    clearChat() {
      this.$store.commit("clearTaraMessages");
      this.inputText = "";
    },

    scrollToBottom() {
      const el = this.$refs.chatWindow;
      if (el) el.scrollTop = el.scrollHeight;
    },

    showError(msg) {
      this.errMsg = msg;
      this.isErr = true;
    },
  },
};
</script>